// src/billing/billingApi.js — the customer's billing surface, /api/v1/billing/*.
//
// ⭐ ONE READ BUILDS THE SCREEN: GET /subscription returns the plan in force,
// every plan as a priced row CARRYING THE ACTION TO OFFER, any pending change
// or scheduled cancellation, the packs on sale, and whether the invoice
// details are complete. ⛔ There is no /plans route any more — it was deleted
// and its catalogue moved inside /subscription.
//
// ⛔ EVERY ROUTE HERE IS CUSTOMER-ADMIN ONLY. 403 CUSTOMER_ADMIN_REQUIRED is
// NOT an error state: hide the controls, no toast.
//
// ⚠️ success_url / cancel_url / return_url are ABSOLUTE http(s) URLs and are
// ours to supply — a relative path is 422 BRIEF_VALIDATION_ERROR. (They used
// to be relative `*_path` values; the backend changed.)
//
// ⚠️ Every price and credit figure on the wire is a Decimal STRING.
// ⚠️ The error envelope is FLAT: body.error_code, never body.detail.error_code.
//
// ⚠️ Cache keying: /subscription is cached by URL only, so every URL here is
// keyed by `?tenant=` (defaulting to the active workspace) and the redundant
// X-Tenant-Id header is suppressed whenever the query carries the scope — the
// same convention as services/api/creditsApi.js.

import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';
import { termsBody } from '../legal/termsModel.js';

export const BILLING = '/api/v1/billing';

// Tolerant unwrap: the contract documents a FLAT envelope, but an older route
// or proxy may still nest it under `detail`. Reading both costs nothing.
async function readError(res) {
  const body = await res.json().catch(() => ({}));
  return body && body.detail && typeof body.detail === 'object' ? body.detail : body;
}

// ⚠️ The thrown error is built by `buildApiError` and carries `code`, `status`
// and `requestId` (a 5xx shows it as a support reference). ⛔ Its `message` is
// the server's English — never rendered; callers word errors from `code` /
// `status` (billing/refusalCopy.js). The screen reads
// `missing_invoice_fields` off the GET /subscription answer, not off a refusal.
function structuredError(envelope, status) {
  return buildApiError(envelope, status, { fallbackMessage: `billing request failed: ${status}` });
}

// The active workspace, from the same localStorage key apiFetch reads for
// X-Tenant-Id — so a tenant-keyed URL and the header can never disagree.
function activeTenantId() {
  try {
    return localStorage.getItem('auth_tenant_id') || '';
  } catch {
    return ''; // private mode
  }
}

function tenantQuery(tenant) {
  const t = tenant || activeTenantId();
  return { t, qs: t ? `?tenant=${encodeURIComponent(t)}` : '' };
}

/**
 * An absolute http(s) URL for one of OUR pages.
 *
 * ⛔ The backend validates this: a relative path, or a `javascript:` URL, is
 * 422 BRIEF_VALIDATION_ERROR (details.validation_errors[].msg). Stripe needs
 * somewhere real to send the browser back to, so there is no sensible default
 * and no server-side fallback any more.
 */
export function absoluteUrl(path) {
  return new URL(path, window.location.origin).toString();
}

/**
 * The plans screen, in one read.
 *
 * ⭐ It never calls Stripe, so it answers in full even where Stripe is
 * unreachable — which is why it carries NO Stripe `status` field. When Stripe
 * gives up, `plan_id` simply becomes null.
 * ⭐ v204 `payment_pending` ({reason, kind, plan_id, hosted_invoice_url, since,
 * expires_at}) is the backend's OWN record of a payment waiting for the
 * customer (3-D Secure, or a declined renewal Stripe is retrying). ⚠️ Its
 * `null` means NOTHING IS WAITING — never "unknown".
 *
 * ⛔ No query parameters beyond the workspace scope — anything else is 422
 * UNKNOWN_QUERY_PARAM.
 *
 * ⚠️ `fresh` bypasses the browser cache (/subscription is cached
 * private,max-age=60) for a re-read after a write or a return from Stripe;
 * ordinary reads should not pass it.
 */
export async function getSubscription({ tenant, fresh = false } = {}) {
  const { t, qs } = tenantQuery(tenant);
  const res = await apiFetch(`${BILLING}/subscription${qs}`, {
    suppressTenantHeader: !!t,
    ...(fresh ? { cache: 'no-store' } : {}),
  });
  if (!res.ok) {
    throw structuredError(await readError(res), res.status);
  }
  return res.json();
}

/** POST helper: tenant-keyed URL, JSON body, flat-envelope errors. */
async function billingPost(path, body, { tenant } = {}) {
  const { t, qs } = tenantQuery(tenant);
  const res = await apiFetch(`${BILLING}${path}${qs}`, {
    method: 'POST',
    suppressTenantHeader: !!t,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    throw structuredError(await readError(res), res.status);
  }
  return res.json();
}

/**
 * FIRST purchase only — a row whose `action` is `start-subscription`.
 * ⛔ A customer already paying through Stripe is refused 409
 * SUBSCRIPTION_ALREADY_ACTIVE; plan changes go to /change-plan.
 * ⚠️ Can take several seconds (it retires a lapsed subscription first) — give
 * the button a pending state, never a short timeout.
 * ⭐ v221: `terms` = the terms the confirmation SHOWED (legal/termsModel) → terms_accepted + the two versions; a stale
 * pair is 409 TERMS_VERSION_CHANGED (after every other refusal, before anything on Stripe).
 */
export async function createCheckoutSession({ planId, successUrl, cancelUrl, terms }, opts = {}) {
  return billingPost('/checkout-session',
    { plan_id: planId, success_url: successUrl, cancel_url: cancelUrl, ...termsBody(terms) }, opts);
}

/**
 * Move to another plan. ⛔ No price in the body: never the client's to name.
 * ⚠️ Read `timing` on the response — the backend decides IMMEDIATE (an
 * upgrade, charged now, restarts the period) vs SCHEDULED (a downgrade at the
 * period end).
 * ⭐ v204 `quote` (from previewChangePlan) makes an IMMEDIATE change charge only the total the user saw: a moved
 * total or a quote older than 5 minutes is 409 QUOTE_EXPIRED, a quote not ours 422 QUOTE_INVALID. Ignored on a
 * scheduled change. Refusals that carry extras (hoisted onto the error by buildApiError):
 * 402 PAYMENT_ACTION_REQUIRED `hosted_invoice_url`, `expires_at`; both 402s and 502 CHANGE_FAILED_CHANGE_RELEASED
 * `released_scheduled_plan_id` (a scheduled change removed BEFORE the charge — it does not come back);
 * 409 PAYMENT_PENDING `hosted_invoice_url`.
 * ⭐ v222: `terms` = the terms the confirmation SHOWED → terms_accepted + the two versions (required by BE ≥ d208cb9,
 * both arms); a stale pair is 409 TERMS_VERSION_CHANGED, the LAST refusal (nothing changed, the quote not spent).
 * ⛔ The preview below never carries them (422).
 */
export async function changePlan(planId, { quote, terms, ...opts } = {}) {
  const body = quote ? { plan_id: planId, quote } : { plan_id: planId };
  return billingPost('/change-plan', { ...body, ...termsBody(terms) }, opts);
}

/**
 * v204 — what an IMMEDIATE change would charge, before the user confirms: `amount` (integers in minor units, from
 * Stripe's own preview), `card` (null = no card / not a card) + `payment_method_type`, `quote` (+ 5-minute
 * `quote_expires_at`), `disclosure_codes`, `discloses`. Same guards and codes as /change-plan; a change that would
 * be scheduled is 409 CHANGE_NOT_IMMEDIATE. Writes nothing.
 */
export async function previewChangePlan(planId, opts = {}) {
  return billingPost('/change-plan/preview', { plan_id: planId }, opts);
}

/**
 * Drop a pending change. ⛔ Its own control, never a change-plan back to the
 * current plan: that would restart the period and charge for time already
 * paid for. Here nothing is charged and the billing date is unchanged.
 */
export async function cancelScheduledChange(opts = {}) {
  return billingPost('/cancel-change', undefined, opts);
}

/** The reasons a customer may give on «Disdici» — the backend's own vocabulary (src/content/departures.py:45-47). */
export const CANCEL_REASON_CODES = ['too_expensive', 'not_used_enough', 'missing_feature', 'switching_tool', 'other'];
/** The backend's limit on `comment` (departures.py:50); longer is 422 and NOTHING is cancelled. */
export const CANCEL_COMMENT_MAX = 1000;

/**
 * Stop the subscription. ⛔⛔ TWO OUTCOMES — branch on the response `status`:
 *   "canceled" → IMMEDIATE (past_due/unpaid); open invoices voided; ⛔ NO resume
 *   anything else → SCHEDULED for the period end; /resume undoes it
 * ⛔ Do not read `cancel_at_period_end` alone: on the immediate path it is false.
 * ⭐ v203: an OPTIONAL reason ({reasonCode, comment}) is stored by the backend; nothing given → no body at all.
 * It releases a pending scheduled change first (`released_scheduled_plan_id`); a 502
 * CANCEL_FAILED_CHANGE_RELEASED means the change is gone but the cancel failed.
 */
export async function cancelSubscription({ reasonCode = null, comment = '', tenant } = {}) {
  const text = (comment || '').trim().slice(0, CANCEL_COMMENT_MAX);
  const body = {
    ...(reasonCode ? { reason_code: reasonCode } : {}),
    ...(text ? { comment: text } : {}),
  };
  return billingPost('/cancel', Object.keys(body).length ? body : undefined, { tenant });
}

/** Undo a SCHEDULED cancellation. Nothing to resume after an immediate one. */
export async function resumeSubscription(opts = {}) {
  return billingPost('/resume', undefined, opts);
}

/**
 * Buy a credit pack. ⛔ `pack_id` comes from the screen's `packs[]` — never
 * hard-coded, never a Price id, never an amount.
 * ⚠️ Its `estimated_*` figures are ESTIMATES read when the session opens; the
 * real ones are fixed at payment. Word them "about X".
 * ⭐ v221: carries the terms acceptance like createCheckoutSession.
 */
export async function createPackSession({ packId, successUrl, cancelUrl, terms }, opts = {}) {
  return billingPost('/pack-session',
    { pack_id: packId, success_url: successUrl, cancel_url: cancelUrl, ...termsBody(terms) }, opts);
}

/**
 * Stripe's hosted card page. ⛔ Card details never touch our UI, and the page
 * offers NO plan changes or cancellation — those are our routes, with our rules.
 * ⚠️ 409 NO_PAYMENT_ACCOUNT until the customer has STARTED a checkout at least
 * once; an operator-assigned plan has none. Hide the button while
 * `subscription_id` is null.
 */
export async function createPortalSession({ returnUrl }, opts = {}) {
  return billingPost('/portal-session', { return_url: returnUrl }, opts);
}
